import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AuthLayout from '../../../components/Auth/AuthLayout';
import LoadingButton from '../../../components/Auth/LoadingButton';

const AirlinerEmailVerificationPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState('');
  const [resendLoading, setResendLoading] = useState(false);

  useEffect(() => {
    // Auto-verify on component mount
    verifyEmail();
  }, []);

  const verifyEmail = async () => {
    setLoading(true);
    setError('');

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Simulate verification success
      setVerified(true);
    } catch (error) {
      setError('Email verification failed. The link may be expired or invalid.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResendLoading(true);
    setError('');

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      alert('Verification email sent! Please check your inbox.');
    } catch (error) {
      setError('Failed to resend verification email. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  const emailIcon = (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
  );

  const checkIcon = (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
  );

  if (loading) {
    return (
      <AuthLayout
        title="Verifying Email"
        subtitle="Please wait while we verify your email address"
        icon={emailIcon}
        iconBgColor="bg-jet-yellow-100"
        iconColor="text-jet-yellow-600"
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-jet-yellow mx-auto mb-4"></div>
          <p className="text-jet-gray-600">Verifying your email address...</p>
        </div>
      </AuthLayout>
    );
  }

  if (verified) {
    return (
      <AuthLayout
        title="Email Verified!"
        subtitle="Your email address has been successfully verified"
        icon={checkIcon}
        iconBgColor="bg-jet-green-100"
        iconColor="text-jet-green-600"
      >
        <div className="text-center space-y-4">
          <p className="text-jet-gray-600">
            Your airliner account is now active. You can proceed to login.
          </p>
          
          <LoadingButton
            onClick={() => navigate('/airliner/auth/login')}
            className="bg-jet-yellow hover:bg-jet-yellow-hover text-jet-gray-900"
          >
            Continue to Login
          </LoadingButton>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Email Verification Failed"
      subtitle="We couldn't verify your email address"
      icon={emailIcon}
      iconBgColor="bg-jet-red-100"
      iconColor="text-jet-red-600"
    >
      <div className="text-center space-y-4">
        <p className="text-jet-gray-600">
          {error || 'The verification link may be expired or invalid.'}
        </p>
        
        <div className="space-y-3">
          <LoadingButton
            onClick={handleResendVerification}
            loading={resendLoading}
            loadingText="Sending..."
            className="bg-jet-yellow hover:bg-jet-yellow-hover text-jet-gray-900"
          >
            Resend Verification Email
          </LoadingButton>
          
          <button
            onClick={() => navigate('/airliner/auth/login')}
            className="w-full border border-jet-gray-300 hover:border-jet-gray-400 text-jet-gray-700 font-medium py-3 px-4 rounded-md transition-colors"
          >
            Back to Login
          </button>
        </div>

        {/* Back to Main Site */}
        <div className="pt-4 border-t border-jet-gray-200">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="text-jet-gray-600 hover:text-jet-gray-900 text-sm transition-colors"
          >
            ← Back to Main Site
          </button>
        </div>
      </div>
    </AuthLayout>
  );
};

export default AirlinerEmailVerificationPage;